import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { getHttpStatus } from "@/utils/httpErrors";
import {
  useUpdateWhatsAppHotelConfig,
  useWhatsAppHotelConfig,
} from "../hooks/whatsapp-provisioning.hooks";

interface WhatsappRuntimeSettingsPanelProps {
  hotelId: string;
  integrationEnabled: boolean;
}

interface RuntimeSettingsDraft {
  liveAutoReplyEnabled: boolean;
  liveHumanHandoffEnabled: boolean;
  systemTemplatesEnabled: boolean;
  reengagementEnabled: boolean;
}

const DEFAULT_RUNTIME_SETTINGS: RuntimeSettingsDraft = {
  liveAutoReplyEnabled: false,
  liveHumanHandoffEnabled: true,
  systemTemplatesEnabled: true,
  reengagementEnabled: true,
};

export const WhatsappRuntimeSettingsPanel: React.FC<
  WhatsappRuntimeSettingsPanelProps
> = ({ hotelId, integrationEnabled }) => {
  const { t } = useTranslation();
  const configQuery = useWhatsAppHotelConfig(hotelId, true);
  const updateConfig = useUpdateWhatsAppHotelConfig(hotelId);
  const isMissingConfig =
    configQuery.isError && getHttpStatus(configQuery.error) === 404;
  const [draft, setDraft] = React.useState<RuntimeSettingsDraft>(
    DEFAULT_RUNTIME_SETTINGS,
  );

  React.useEffect(() => {
    if (!configQuery.data) return;
    setDraft({
      liveAutoReplyEnabled: configQuery.data.liveAutoReplyEnabled,
      liveHumanHandoffEnabled: configQuery.data.liveHumanHandoffEnabled,
      systemTemplatesEnabled: configQuery.data.systemTemplatesEnabled,
      reengagementEnabled: configQuery.data.reengagementEnabled,
    });
  }, [configQuery.data]);

  const baseline = configQuery.data
    ? {
        liveAutoReplyEnabled: configQuery.data.liveAutoReplyEnabled,
        liveHumanHandoffEnabled: configQuery.data.liveHumanHandoffEnabled,
        systemTemplatesEnabled: configQuery.data.systemTemplatesEnabled,
        reengagementEnabled: configQuery.data.reengagementEnabled,
      }
    : DEFAULT_RUNTIME_SETTINGS;
  const isDirty =
    draft.liveAutoReplyEnabled !== baseline.liveAutoReplyEnabled ||
    draft.liveHumanHandoffEnabled !== baseline.liveHumanHandoffEnabled ||
    draft.systemTemplatesEnabled !== baseline.systemTemplatesEnabled ||
    draft.reengagementEnabled !== baseline.reengagementEnabled ||
    isMissingConfig;
  const isBusy = configQuery.isFetching || updateConfig.isPending;

  const handleSave = () => {
    updateConfig.mutate(
      {
        enabled: configQuery.data?.enabled ?? integrationEnabled,
        ...draft,
      },
      {
        onSuccess: () =>
          toast.success(
            t("communicationChannels.whatsapp.runtimeSettings.saveSuccess"),
          ),
        onError: () =>
          toast.error(
            t("communicationChannels.whatsapp.runtimeSettings.saveError"),
          ),
      },
    );
  };

  return (
    <section className="flex flex-col gap-4 border-t pt-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <h4 className="text-sm font-medium text-foreground">
            {t("communicationChannels.whatsapp.runtimeSettings.title")}
          </h4>
          <p className="text-xs text-muted-foreground">
            {t("communicationChannels.whatsapp.runtimeSettings.description")}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="new_outline"
            size="sm"
            disabled={isBusy}
            onClick={() => configQuery.refetch()}
          >
            <RefreshCw
              className={
                configQuery.isFetching ? "size-4 animate-spin" : "size-4"
              }
            />
            {t("communicationChannels.whatsapp.runtimeSettings.refresh")}
          </Button>
          <Button
            type="button"
            variant="new_default"
            size="sm"
            disabled={isBusy || !isDirty}
            onClick={handleSave}
          >
            {updateConfig.isPending && (
              <Loader2 className="size-4 animate-spin" />
            )}
            {updateConfig.isPending
              ? t("communicationChannels.whatsapp.runtimeSettings.saving")
              : t("communicationChannels.whatsapp.runtimeSettings.save")}
          </Button>
        </div>
      </div>

      {configQuery.isLoading && (
        <p className="text-xs text-muted-foreground">
          {t("communicationChannels.whatsapp.runtimeSettings.loading")}
        </p>
      )}

      {configQuery.isError && !isMissingConfig && (
        <p className="text-xs text-destructive">
          {t("communicationChannels.whatsapp.runtimeSettings.loadError")}
        </p>
      )}

      {isMissingConfig && (
        <p className="text-xs text-muted-foreground">
          {t("communicationChannels.whatsapp.runtimeSettings.missingConfig")}
        </p>
      )}

      {(!configQuery.isLoading || isMissingConfig) &&
        (!configQuery.isError || isMissingConfig) && (
          <div className="grid gap-2 md:grid-cols-2">
            <RuntimeSettingRow
              label={t(
                "communicationChannels.whatsapp.runtimeSettings.systemTemplates.label",
              )}
              description={t(
                "communicationChannels.whatsapp.runtimeSettings.systemTemplates.description",
              )}
              checked={draft.systemTemplatesEnabled}
              disabled={isBusy}
              onCheckedChange={(value) =>
                setDraft((current) => ({
                  ...current,
                  systemTemplatesEnabled: value,
                }))
              }
            />
            <RuntimeSettingRow
              label={t(
                "communicationChannels.whatsapp.runtimeSettings.reengagement.label",
              )}
              description={t(
                "communicationChannels.whatsapp.runtimeSettings.reengagement.description",
              )}
              checked={draft.reengagementEnabled}
              disabled={isBusy}
              onCheckedChange={(value) =>
                setDraft((current) => ({
                  ...current,
                  reengagementEnabled: value,
                }))
              }
            />
            <RuntimeSettingRow
              label={t(
                "communicationChannels.whatsapp.runtimeSettings.humanHandoff.label",
              )}
              description={t(
                "communicationChannels.whatsapp.runtimeSettings.humanHandoff.description",
              )}
              checked={draft.liveHumanHandoffEnabled}
              disabled={isBusy}
              onCheckedChange={(value) =>
                setDraft((current) => ({
                  ...current,
                  liveHumanHandoffEnabled: value,
                }))
              }
            />
            <RuntimeSettingRow
              label={t(
                "communicationChannels.whatsapp.runtimeSettings.aiAutoReply.label",
              )}
              description={t(
                "communicationChannels.whatsapp.runtimeSettings.aiAutoReply.description",
              )}
              checked={draft.liveAutoReplyEnabled}
              disabled={isBusy}
              onCheckedChange={(value) =>
                setDraft((current) => ({
                  ...current,
                  liveAutoReplyEnabled: value,
                }))
              }
            />
          </div>
        )}
    </section>
  );
};

interface RuntimeSettingRowProps {
  label: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onCheckedChange: (value: boolean) => void;
}

const RuntimeSettingRow: React.FC<RuntimeSettingRowProps> = ({
  label,
  description,
  checked,
  disabled,
  onCheckedChange,
}) => (
  <div className="flex items-start justify-between gap-3 rounded-md border bg-background p-3">
    <div className="flex min-w-0 flex-col gap-1">
      <span className="text-sm font-medium text-foreground">{label}</span>
      <span className="text-xs text-muted-foreground">{description}</span>
    </div>
    <Switch
      className="mt-0.5"
      checked={checked}
      disabled={disabled}
      onCheckedChange={onCheckedChange}
      aria-label={label}
    />
  </div>
);
